import {
  SMS_SEGMENT_LEN_GSM7,
  SMS_CONCAT_LEN_GSM7,
  SMS_SEGMENT_LEN_UCS2,
  SMS_CONCAT_LEN_UCS2,
} from './constants';

/**
 * GSM 03.38 encoding detection and SMS segmentation.
 *
 * Character tables are transcribed from 3GPP TS 23.038 (originally GSM 03.38),
 * "Alphabets and language-specific information":
 *   - clause 6.2.1   "GSM 7 bit Default Alphabet"            -> GSM7_BASIC
 *   - clause 6.2.1.1 "GSM 7 bit default alphabet extension table" -> GSM7_EXTENDED
 * Spec archive: https://www.3gpp.org/ftp/Specs/archive/23_series/23.038/
 *
 * Verified character-for-character against Twilio's independently maintained table in
 * `sms-segments-calculator` (TwilioDevEd/message-segment-calculator): 127 basic + 10
 * extension entries, no differences either way. Segment counts were fuzzed against the
 * same library over 419 inputs; the only divergence is its CRLF handling, where
 * grapheme-splitter merges \r\n into one cluster and bills it as a single 16-bit unit
 * instead of two. A textarea normalises its value to \n, so that case cannot reach us.
 *
 * Mirrors system2-server's `functions-22/src/utils/smsEncoding.ts`, which decides the
 * `unicodeEnabled` / `unicode_sms` flags we send to Brevo. That copy is authoritative —
 * it is what gets billed. This one exists so the composer can show the cost before sending.
 */

// GSM 03.38 basic character set. Each of these costs one septet.
const GSM7_BASIC = '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?'
  + '¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';

// GSM 03.38 extension table. Still GSM-7, but each costs two septets (an escape byte
// plus the character itself).
const GSM7_EXTENDED = '\f^{}\\[~]|€';

const GSM7_BASIC_CHARS = new Set(GSM7_BASIC);
const GSM7_EXTENDED_CHARS = new Set(GSM7_EXTENDED);

export const ENCODING = {
  GSM7: 'GSM-7',
  UCS2: 'Unicode',
};

/**
 * Whether every character in the text can be sent as GSM-7.
 *
 * `Array.from` walks code points rather than UTF-16 units. That is deliberate: any
 * component of a multi-code-point emoji is itself outside the GSM-7 alphabet, so splitting
 * one apart cannot produce a false positive.
 *
 * @param {string} text The message text to classify.
 * @returns {boolean} True when the text needs no unicode escape hatch.
 */
export const isGsm7 = (text) => Array.from(text)
  .every((char) => GSM7_BASIC_CHARS.has(char) || GSM7_EXTENDED_CHARS.has(char));

const splitInto = (units, singleLen, concatLen) => {
  if (units === 0) return { perSegment: singleLen, segments: 0 };
  if (units <= singleLen) return { perSegment: singleLen, segments: 1 };
  return { perSegment: concatLen, segments: Math.ceil(units / concatLen) };
};

/**
 * How the carrier will encode, split and bill this text.
 *
 * Units are UTF-16 code units, so a non-BMP emoji counts as 2 and a ZWJ sequence considerably
 * more — which is what a unicode message is actually billed on.
 *
 * Deliberately not exact for GSM-7: an extension-table character (`€ ^ { } [ ] ~ | \\`) really
 * costs two septets, but weighting for that buys ±2 characters of accuracy on a figure whose
 * inputs are already ±20 — the link is a 28-character stand-in for a Brevo short URL we have
 * never measured, and the first name is a 9-character sample. Across all 2,016 campaign bodies
 * and re-engagement blasts in prod, weighting changed the segment count exactly zero times.
 *
 * `segments` is the number the composer shows the CAM, because that is what gets billed — per
 * segment, per recipient. `perSegment` exists to make that number meaningful (and to keep the
 * split arithmetic in one place); the composer does not count against it. Brevo's own editor
 * grows its allowance as the message spills into more parts, and the composer deliberately does
 * not follow: a denominator derived from the numerator can never look expensive, and it read as
 * roomier at exactly the moment the split warning fired. The composer counts against the size
 * of one SMS instead — see `counterMax` in `components/MessageBodyField.jsx`. There is no
 * campaign-wide character cap; Brevo's UI does not surface one.
 *
 * @param {string} text The full message as the recipient will receive it. Classified for
 * encoding, and measured for length unless `units` says otherwise.
 * @param {number} [units] Billed length, when it differs from `text.length`. The caller may
 * know the delivered message is longer than the string it can render — see
 * `substitutedSegments` — and the segment count has to follow the billed figure, not the
 * rendered one. Only ever differs by ASCII separators, so it cannot change the encoding.
 * @returns {{encoding: string, units: number, perSegment: number, segments: number}}
 */
export const countSmsSegments = (text, units = text.length) => {
  const gsm7 = isGsm7(text);
  const [singleLen, concatLen] = gsm7
    ? [SMS_SEGMENT_LEN_GSM7, SMS_CONCAT_LEN_GSM7]
    : [SMS_SEGMENT_LEN_UCS2, SMS_CONCAT_LEN_UCS2];

  return {
    encoding: gsm7 ? ENCODING.GSM7 : ENCODING.UCS2,
    units,
    ...splitInto(units, singleLen, concatLen),
  };
};
